import React, {
 useState, useEffect, useCallback, useMemo, useRef,
} from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

import { hiddenByMute } from 'views_shared/app/javascript/api/pitches';
import withV2HealthCheck from '../with_v2_health_check';
import withV2ProfileSlug from './withV2ProfileSlug';

import ConversationHeader from './ConversationHeader';
import usePitchedEntities from './usePitchedEntities';
import ConversationSidebar from './ConversationSidebar';
import ConversationsList from './ConversationsList';
import ConversationsToolbar from './ConversationsToolbar';
import InboxDropdownMenu from './InboxDropdownMenu';
import InboxEmptyState from './InboxEmptyState/InboxEmptyState';
import MessageComposer from './MessageComposer';
import Messages from './Messages';
import { MessagesProvider } from './MessagesContext';
import { MailboxTabs, useMailbox } from './MailboxTabs';
import { inboxPath, readComposeBody, stripComposeBodyFromUrl } from './composeUrl';
import mergeFeedRows from './mergeFeedRows';
import PitchThreadPane from './PitchThreadPane';
import { SelectionBar, useSelectionMode } from './SelectionBar';
import useComposeDraft from './useComposeDraft';
import useConversations from './useConversations';
import useVirtualPitchRows from './useVirtualPitchRows';
import useDeepLinkResolver from './useDeepLinkResolver';
import useIsDesktop from './useIsDesktop';
import usePitchDeepLink from './usePitchDeepLink';
import useSearchConversations from './useSearchConversations';
import useSidebar from './useSidebar';
import useUrlReflection from './useUrlReflection';

function MyInbox() {
  const currentReceiverSlug = globalThis.Qwoted.CURRENT_SLUG;
  // Conversation list/sync queries key on the stable integer profile id, not the slug
  // (slugs are friendly IDs that change on profile rename). Resolved by withV2ProfileSlug.
  const currentReceiverProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // Only media users see the Focused tab; promoters get Inbox + Trash only.
  const isMedia = globalThis.Qwoted.CURRENT_PROFILE_TYPE === 'type_media';

  // V1-MIGRATION: mute/report state for the pitch menus, lifted here exactly like the
  // request page's PitchInboxSection lifts it — every mounted menu for the same pitcher
  // stays in sync because they all read this one state. The page global carries the
  // server config plus the reporter's current muted-user / reported-pitch id lists.
  const muteReportConfig = globalThis.Qwoted.MUTE_REPORT || null;
  const [mutedUserIds, setMutedUserIds] = useState(() => muteReportConfig?.mutedUserIds || []);
  const [mutes, setMutes] = useState(() => muteReportConfig?.mutes || []);
  const [reportedPitchIds, setReportedPitchIds] = useState(() => muteReportConfig?.reportedPitchIds || []);
  const muteReport = useMemo(() => {
    if (!muteReportConfig) return null;
    return {
      reportingUserId: muteReportConfig.reportingUserId,
      defaultMuteDurationDays: muteReportConfig.defaultMuteDurationDays,
      abusiveBehaviorTypes: muteReportConfig.abusiveBehaviorTypes,
      mutedUserIds,
      reportedPitchIds,
      onMutedChange: (userId, nowMuted, mute = null) => {
        setMutedUserIds((prev) => (nowMuted ? [...prev, userId] : prev.filter((id) => id !== userId)));
        setMutes((prev) => {
          const others = prev.filter((m) => m.userId !== userId);
          if (!nowMuted) return others;
          return [...others, {
            userId,
            createdAt: mute?.created_at || new Date().toISOString(),
            durationDays: mute?.duration_days ?? muteReportConfig.defaultMuteDurationDays,
          }];
        });
      },
      onReported: (pitchId) => setReportedPitchIds((prev) => [...prev, pitchId]),
    };
  }, [muteReportConfig, mutedUserIds, reportedPitchIds]);

  const isDesktop = useIsDesktop();

  // Compose (ephemeral draft) mode: "Start a Conversation" in the source directory lands here with
  // ?compose_origin_* params. `composeActive` is true synchronously from the URL; `draftConversation`
  // fills in once the recipients resolve. While composing we keep the draft in the right pane and
  // stop the list from auto-selecting a real thread over it.
  const {
 composeActive, draftConversation, isResolvingDraft, finishCompose,
} = useComposeDraft(currentReceiverProfileId);

  // A canned body to pre-fill the composer with, from the compose URL. Present both when composing a
  // new thread (?compose_origin_*&compose_body) and when deep-linking into an existing one
  // (?conversation=<uuid>&compose_body) — Request Sample pre-fills either way. Read once at mount so
  // it seeds the composer's initial content before it renders/focuses.
  const [initialMessageContent] = useState(() => readComposeBody());
  // For an existing-thread deep-link the body has now been captured; drop it from the URL so a reload
  // doesn't re-seed and the address bar stays clean. Compose mode strips its own params on send.
  useEffect(() => {
    if (!composeActive) stripComposeBodyFromUrl();
  }, [composeActive]);

  const [searchQuery, setSearchQuery] = useState(null);
  const { sidebarExpanded, toggleSidebar } = useSidebar(currentReceiverSlug, isDesktop);
  const [activeFilters, setActiveFilters] = useState({ role: null, status: null, posts: null });

  const {
 refreshCounts, decrementFocusedCount, decrementEverythingCount, ...mailbox
} = useMailbox(
    currentReceiverSlug,
    currentReceiverProfileId,
    isMedia,
  );

  // Computed before useConversations: while the merged Everything feed is active, MyInbox
  // owns auto-selection (a virtual pitch row can outrank conversations[0]), so the
  // conversations hook must not auto-select on its own. Suppression covers the WHOLE
  // media-Everything context including search — a background fresh fetch during search
  // must not auto-select a conversation under a selected search-result pitch.
  const pitchScopeActive = isMedia && !mailbox.showTrashed;
  const everythingFeedActive = pitchScopeActive && mailbox.activeMailbox === 'everything';
  const virtualRowsEnabled = everythingFeedActive && !searchQuery;

  const handleMarkRead = useCallback(
    (conversation) => {
      if (isMedia && conversation?.conversation_mailbox === 'focused') {
        decrementFocusedCount();
      }
      decrementEverythingCount();
    },
    [isMedia, decrementFocusedCount, decrementEverythingCount],
  );

  // Conversations state
  const {
    conversations,
    allIds: allConversationIds,
    selectedConversation,
    selectedConversationRef,
    selectConversation,
    markSelectedAsRead,
    refetch: refetchConversations,
    loadMore: loadMoreConversations,
    evictConversations,
    patchConversations,
    markAllLoadedAsRead,
    syncConversation,
    hasMore: hasMoreConversations,
    isLoadingMore: isLoadingMoreConversations,
    isLoading: isLoadingConversations,
    isWaitingForDeepLink,
  } = useConversations(
    currentReceiverProfileId,
    mailbox.mailboxFilter,
    activeFilters,
    handleMarkRead,
    isDesktop,
    mailbox.showTrashed,
    composeActive || everythingFeedActive,
  );

  // Promoter mode hides the Role chip — a stale `role` filter value in state
  // shouldn't count as "active" for the empty-state messaging there.
  const hasActiveFilters = Boolean((isMedia && activeFilters.role) || activeFilters.status || activeFilters.posts);

  // ── Virtual pitch rows (Everything tab): conversationless pitches on my requests ──
  // Filters apply to pitches with the same semantics as conversations (status → read_at,
  // role → the pitcher's profile type, posts → the pitch's request title) — both streams
  // narrow symmetrically, so the merged feed still paginates correctly. Search pauses the
  // stream, but search RESULTS carry their own pitch rows (the endpoint's pitch arm) —
  // so a selected pitch survives searching; only leaving the pitch scope clears it.
  const [selectedPitch, setSelectedPitch] = useState(null);
  // V1-MIGRATION: the reporter's mutes ride along as a filter, so V2 never serves the pitch
  // rows the request page would hide (SourceRequest.filter_muted_pitches). Muting from a row
  // changes the filter, which refetches — the row disappears like it does on the request page.
  const pitchFilters = useMemo(
    () => (mutes.length ? { ...activeFilters, mutes } : activeFilters),
    [activeFilters, mutes],
  );
  const virtualPitches = useVirtualPitchRows(currentReceiverProfileId, virtualRowsEnabled, pitchFilters);
  const {
 loadMore: loadMorePitches, markPitchRead, markAllPitchesRead, injectPitch,
} = virtualPitches;

  // A selected pitch only makes sense in the Everything tab's merged feed (search within
  // the tab keeps it — search results carry their own pitch rows).
  useEffect(() => {
    if (!pitchScopeActive || mailbox.activeMailbox !== 'everything') setSelectedPitch(null);
  }, [pitchScopeActive, mailbox.activeMailbox]);

  // The selected row is pinned through the merge's frontier cut so a deep selection
  // (deep link / carried across tabs) stays visible past either stream's pagination.
  const pinnedRowIds = useMemo(() => {
    const ids = [];
    if (selectedConversation) ids.push(selectedConversation.conversation_id);
    if (selectedPitch) ids.push(`pitch-${selectedPitch.id}`);
    return ids;
  }, [selectedConversation, selectedPitch]);

  const mergedFeed = useMemo(
    () => mergeFeedRows({
        conversations,
        conversationsHasMore: hasMoreConversations,
        pitchRows: virtualRowsEnabled ? virtualPitches.pitchRows : [],
        pitchFrontier: virtualRowsEnabled ? virtualPitches.frontier : null,
        pinnedRowIds,
      }),
    [
      conversations,
      hasMoreConversations,
      virtualRowsEnabled,
      virtualPitches.pitchRows,
      virtualPitches.frontier,
      pinnedRowIds,
    ],
  );

  // Advance whichever stream is blocking the merged feed at the cut.
  const handleLoadMore = useCallback(() => {
    if (!virtualRowsEnabled) {
      loadMoreConversations();
      return;
    }
    if (mergedFeed.advanceConversations) loadMoreConversations();
    if (mergedFeed.advancePitches) loadMorePitches();
  }, [
    virtualRowsEnabled,
    mergedFeed.advanceConversations,
    mergedFeed.advancePitches,
    loadMoreConversations,
    loadMorePitches,
  ]);

  const handleSelectConversation = useCallback(
    (conversation) => {
      setSelectedPitch(null);
      selectConversation(conversation);
    },
    [selectConversation],
  );

  // Selecting a search result EXITS search: the tabs come back on the result's home tab
  // (trash if trashed, else its mailbox) with the item selected — search spans every tab,
  // so the result decides where you land.
  const homeTabFor = useCallback(
    (conversation) => {
      if (conversation.receipt_archived_at) return 'trash';
      if (isMedia && conversation.conversation_mailbox === 'focused') return 'focused';
      return 'everything';
    },
    [isMedia],
  );

  const handleSelectSearchResult = useCallback(
    (conversation) => {
      const target = homeTabFor(conversation);
      if (target !== mailbox.activeMailbox) mailbox.selectMailbox(target);
      setSearchQuery(null);
      setSelectedPitch(null);
      selectConversation(conversation);
    },
    [homeTabFor, mailbox, selectConversation],
  );

  // Quiet selection — used by auto-select, URL reflection, and the deep-link resolver,
  // none of which may mark the pitch read (that takes a deliberate click).
  const selectPitchQuietly = useCallback(
    (pitch) => {
      if (pitch) selectConversation(null);
      setSelectedPitch(pitch);
    },
    [selectConversation],
  );

  const selectedPitchRef = useRef(selectedPitch);
  selectedPitchRef.current = selectedPitch;

  // A filter change evicts a selected pitch that no longer matches — the pitch counterpart
  // of useConversations' convMatchesFilters clearing. Keyed on filter changes only (via the
  // ref), so an in-place read-stamp under "Unread only" doesn't yank the pane mid-read —
  // same feel as conversations, which evaluate membership at fetch time.
  useEffect(() => {
    const pitch = selectedPitchRef.current;
    if (pitch && !qapi.pitches.matchesFilters(pitch, pitchFilters)) setSelectedPitch(null);
  }, [pitchFilters]);

  // Preserve+inject for pitches (useConversations does this for its selection on every
  // fresh fetch): a fresh pitch fetch replaces the stream, which would drop a deep-link
  // row deeper than page 1 — re-inject the selected pitch whenever its row is missing.
  useEffect(() => {
    if (!selectedPitch || !virtualRowsEnabled || virtualPitches.isLoading) return;
    if (!virtualPitches.pitchRows.some((p) => p.id === selectedPitch.id)) {
      injectPitch(selectedPitch);
    }
  }, [selectedPitch, virtualRowsEnabled, virtualPitches.pitchRows, virtualPitches.isLoading, injectPitch]);

  // Opening a virtual pitch row marks the pitch read (server + optimistic stamp) — the
  // inbox-side counterpart of the request page's bulk read-marking on visit. The stamped
  // copy goes into selectedPitch so the pane-click handler doesn't re-fire the PATCH.
  // Search results render pitch rows in ANY tab (the search endpoint is tab-agnostic), so
  // selecting one moves to Everything — the only tab where a pitch selection has a home.
  const { activeMailbox, selectMailbox } = mailbox;
  const handleSelectPitchRow = useCallback(
    (pitch) => {
      if (activeMailbox !== 'everything') selectMailbox('everything');
      if (pitch.read_at) {
        selectPitchQuietly(pitch);
        return;
      }
      markPitchRead(pitch.id);
      decrementEverythingCount();
      qapi.pitches.markReadById(pitch.id).then(({ error }) => {
        if (error) {
          qlog.error('Failed to mark pitch as read:', error);
        }
      });
      selectPitchQuietly({ ...pitch, read_at: new Date().toISOString() });
    },
    [selectPitchQuietly, markPitchRead, decrementEverythingCount, activeMailbox, selectMailbox],
  );

  const handleSelectPitchSearchResult = useCallback(
    (pitch) => {
      setSearchQuery(null);
      handleSelectPitchRow(pitch);
    },
    [handleSelectPitchRow],
  );

  // First send creates the conversation server-side; once the refetched list carries it,
  // selection migrates from the virtual pitch row to the real conversation.
  const selectedPitchConversation = useMemo(() => {
    if (!selectedPitch) return null;
    return conversations.find((c) => c.origin_type === 'Interact::Pitch' && c.origin_id === selectedPitch.id) || null;
  }, [selectedPitch, conversations]);

  useEffect(() => {
    if (selectedPitch && selectedPitchConversation) {
      setSelectedPitch(null);
      selectConversation(selectedPitchConversation);
    }
  }, [selectedPitch, selectedPitchConversation, selectConversation]);

  // MyInbox owns desktop auto-selection while the merged feed is active (useConversations'
  // own auto-select is suppressed then, because a virtual pitch row can outrank its
  // conversations[0]): pick the top merged row, pitch or conversation. Pitch picks never
  // mark read (parity: conversations' auto-select doesn't either; reading takes a real
  // click). One decision per view, reset on tab/filter change; a selection carried over
  // from the previous view (or a resolved deep link) always stands.
  const topRowAutoSelectDoneRef = useRef(false);
  // Deep-link detection must read the URL AT MOUNT — useUrlReflection writes
  // ?conversation=/?pitch= right after auto-select, which is not a deep link. A deep link
  // owns only the view it landed on; navigating tabs/filters spends it. An unresolvable
  // link stops blocking after a grace period (mirrors DEEP_LINK_WAIT_TIMEOUT_MS); a
  // resolved one produced a selection by then, which stands like any other.
  const hadDeepLinkAtMountRef = useRef(
    new URLSearchParams(window.location.search).has('conversation')
      || new URLSearchParams(window.location.search).has('pitch'),
  );
  const [deepLinkGraceOver, setDeepLinkGraceOver] = useState(!hadDeepLinkAtMountRef.current);
  useEffect(() => {
    if (!hadDeepLinkAtMountRef.current) return undefined;
    const timeoutId = setTimeout(() => setDeepLinkGraceOver(true), 10000);
    return () => clearTimeout(timeoutId);
  }, []);

  const viewChangedOnceRef = useRef(false);
  useEffect(() => {
    topRowAutoSelectDoneRef.current = false;
    if (viewChangedOnceRef.current) hadDeepLinkAtMountRef.current = false;
    viewChangedOnceRef.current = true;
  }, [mailbox.activeMailbox, mailbox.showTrashed, activeFilters]);

  useEffect(() => {
    if (topRowAutoSelectDoneRef.current) return;
    if (!isDesktop || !virtualRowsEnabled || composeActive) return;
    if (isLoadingConversations || virtualPitches.isLoading || isWaitingForDeepLink) return;
    if (hadDeepLinkAtMountRef.current && !deepLinkGraceOver) return;

    const first = mergedFeed.rows[0];
    if (!first) return;
    topRowAutoSelectDoneRef.current = true;
    // A selection that survived into this view stands — continuity beats the top row.
    // (Trash/filters clear non-matching selections upstream, so "nothing survived" is
    // exactly the case where the top merged row takes over.)
    if (selectedConversation || selectedPitch) return;

    if (first.feed_row_type === 'pitch') {
      selectPitchQuietly(first.pitch);
    } else {
      selectConversation(first);
    }
  }, [
    isDesktop,
    virtualRowsEnabled,
    composeActive,
    isLoadingConversations,
    virtualPitches.isLoading,
    isWaitingForDeepLink,
    deepLinkGraceOver,
    selectedPitch,
    mergedFeed.rows,
    selectedConversation,
    selectPitchQuietly,
    selectConversation,
  ]);

  // Sync URL ↔ selection (both kinds: ?conversation=<uuid> and ?pitch=<id>): writes on
  // select, listens for popstate (back/forward) to re-select. Runs before the resolvers
  // so the same popstate tick updates selection first; the resolvers can skip fetching.
  useUrlReflection({
    selectedConversation,
    conversations,
    // handleSelectConversation, not the raw setter: a Back from ?pitch= to ?conversation=
    // must clear the pitch selection or the pitch pane keeps the render precedence and the
    // next URL write carries both params.
    selectConversation: handleSelectConversation,
    selectedPitch,
    pitchRows: virtualPitches.pitchRows,
    selectPitch: selectPitchQuietly,
  });

  // Resolve inbound deep-links to the right mailbox — mount + popstate for URLs not
  // yet in the current list slice (other mailbox, Turbolinks back/forward, etc.).
  useDeepLinkResolver({
    currentReceiverProfileId,
    activeMailbox: mailbox.activeMailbox,
    selectMailbox: mailbox.selectMailbox,
    selectConversation: handleSelectConversation,
    selectedConversationRef,
  });

  // Resolve inbound ?pitch=<id> deep-links (virtual pitch rows) — never marks read; a
  // pitch that converged to a conversation is rewritten to its ?conversation= URL.
  usePitchDeepLink({
    currentReceiverProfileId,
    isMedia,
    activeMailbox: mailbox.activeMailbox,
    selectMailbox: mailbox.selectMailbox,
    selectedPitchRef,
    selectPitch: selectPitchQuietly,
    injectPitch: virtualPitches.injectPitch,
  });

  // selectedConversation is not updated on mark-as-read, so components that need
  // fresh fields like receipt_read_at use this derived value instead.
  const liveSelectedConversation = useMemo(() => {
    if (!selectedConversation) return null;
    return (
      conversations.find((c) => c.conversation_id === selectedConversation.conversation_id) || selectedConversation
    );
  }, [selectedConversation, conversations]);

  // Right pane shows the ephemeral draft while composing, otherwise the selected conversation. The
  // draft mimics the conversations-view shape (senders/receivers/origin) so the header, Messages,
  // and composer all render against it unchanged.
  const rightPaneConversation = draftConversation || liveSelectedConversation;
  const messagePaneConversation = draftConversation || selectedConversation;
  const headerPitchedEntities = usePitchedEntities(messagePaneConversation);

  const refetchConversationsAndCounts = useCallback(() => {
    refetchConversations();
    refreshCounts();
  }, [refetchConversations, refreshCounts]);

  // Realtime / post-send update. With a conversation id, sync just that row (cheap);
  // without one, fall back to a full refetch. Counts always refresh.
  const handleConversationUpdate = useCallback(
    (conversationId, eventType) => {
      if (conversationId) syncConversation(conversationId, eventType);
      else refetchConversations();
      refreshCounts();
    },
    [syncConversation, refetchConversations, refreshCounts],
  );

  // First message of a compose draft just created the real conversation. Leave compose mode, then
  // read the new row back and select it so the user keeps chatting in place (selecting reflects
  // ?conversation=<uuid> into the URL). If the row can't be read yet, hard-navigate so the user is
  // never stranded on a now-dead draft.
  const handleDraftCreated = useCallback(
    async (newConversationId) => {
      finishCompose();
      refreshCounts();
      const { data } = await qapi.conversations
        .findById({ receiverProfileId: currentReceiverProfileId, v2Uuid: newConversationId })
        .catch(() => ({}));
      const conv = data?.[0];
      if (conv) {
        selectConversation(conv);
        return;
      }
      window.location.href = `${inboxPath()}?conversation=${newConversationId}`;
    },
    [finishCompose, refreshCounts, currentReceiverProfileId, selectConversation],
  );

  // "Mark All as Read" patches every loaded conv locally first so they update visually
  // even when on pages 2+ (where the page-1 refetch wouldn't reach them). The server call
  // also stamps every pitch on the user's requests, so stamp the loaded virtual rows too.
  const handleMarkAllReadComplete = useCallback(() => {
    markAllLoadedAsRead();
    markAllPitchesRead();
    refetchConversationsAndCounts();
  }, [markAllLoadedAsRead, markAllPitchesRead, refetchConversationsAndCounts]);

  // Action callback: { evictedIds } removes rows; { patches } applies field updates
  // (filter-aware in useConversations). Either is optional — empty options just refetches.
  const handleConversationActionComplete = useCallback(
    (options = {}) => {
      const { evictedIds, patches } = options;
      if (evictedIds && evictedIds.length > 0) evictConversations(evictedIds);
      if (patches && patches.length > 0) patchConversations(patches);
      refetchConversationsAndCounts();
    },
    [evictConversations, patchConversations, refetchConversationsAndCounts],
  );

  // Selection mode state and handlers
  // Bulk-manage selects over the merged feed: conversation UUIDs plus "pitch-<id>" rows.
  const allSelectableIds = useMemo(
    () => (virtualRowsEnabled ? mergedFeed.rows.map((r) => r.conversation_id) : allConversationIds),
    [virtualRowsEnabled, mergedFeed.rows, allConversationIds],
  );
  const handlePitchesMarkedRead = useCallback(
    (pitchIds) => pitchIds.forEach((id) => markPitchRead(id)),
    [markPitchRead],
  );
  const selection = useSelectionMode(
    allSelectableIds,
    handleConversationActionComplete,
    mailbox.mailboxFilter,
    handlePitchesMarkedRead,
  );
  const { selectionMode, toggleSelectionMode } = selection;

  // Bulk manage does not apply to Trash — exit selection mode when switching to that tab.
  useEffect(() => {
    if (mailbox.showTrashed && selectionMode) toggleSelectionMode();
  }, [mailbox.showTrashed, selectionMode, toggleSelectionMode]);

  // Search results (normalized to conversation shape)
  const {
    searchResults,
    isSearching,
    hasMore: hasMoreSearchResults,
    isLoadingMore: isLoadingMoreSearchResults,
    loadMore: loadMoreSearchResults,
  } = useSearchConversations(
    searchQuery,
    currentReceiverSlug,
    { ...activeFilters, role: isMedia ? activeFilters.role : null },
  );
  // Search's pitch arm runs on V2 too, so the mute rule is applied to its hits here
  const visibleSearchResults = useMemo(
    () => searchResults.filter((row) => row.feed_row_type !== 'pitch' || !hiddenByMute(row.pitch, mutes)),
    [searchResults, mutes],
  );

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
  };

  const clearActiveFilters = () => setActiveFilters({ role: null, status: null, posts: null });

  // Truly-empty inbox: no selection, no data, no in-flight fetch, no search/filter narrowing — and
  // not composing (the draft owns the right pane, so the empty state must not replace the composer).
  const showInboxEmptyState = !composeActive
    && !selectedConversation
    && !selectedPitch
    && mergedFeed.rows.length === 0
    && !isLoadingConversations
    && !(virtualRowsEnabled && virtualPitches.isLoading)
    && !hasActiveFilters
    && !searchQuery;

  const handleConversationAreaClick = useCallback(() => {
    // Mobile: don't auto-mark-read on tap — a tap can be exploratory
    if (isDesktop && selectedConversation) {
      markSelectedAsRead();
    }
    // Auto-selected pitch: the first real click in the pane marks it read (row clicks
    // already mark in handleSelectPitchRow; this covers the auto-select path).
    if (isDesktop && selectedPitch && !selectedPitch.read_at) {
      const stamp = new Date().toISOString();
      setSelectedPitch((prev) => (prev && !prev.read_at ? { ...prev, read_at: stamp } : prev));
      markPitchRead(selectedPitch.id);
      decrementEverythingCount();
      qapi.pitches.markReadById(selectedPitch.id).then(({ error }) => {
        if (error) {
          qlog.error('Failed to mark pitch as read:', error);
        }
      });
    }
  }, [isDesktop, selectedConversation, markSelectedAsRead, selectedPitch, markPitchRead, decrementEverythingCount]);

  const clearSelection = useCallback(() => selectConversation(null), [selectConversation]);

  // Which pane is visible on mobile/tablet (ignored on lg+)
  let mobileView = 'list';
  if (selectedConversation || selectedPitch) {
    mobileView = sidebarExpanded ? 'sidebar' : 'messages';
  } else if (composeActive) {
    // Composing: show the messages/composer pane so the first message can be typed on mobile too.
    mobileView = 'messages';
  }

  return (
    <div className="my-inbox d-flex flex-column flex-grow-1" id="my-inbox" data-mobile-view={mobileView}>
      <MessagesProvider
        conversationId={draftConversation ? undefined : selectedConversation?.conversation_id}
        composeDraft={draftConversation}
        initialMessageContent={initialMessageContent}
        onDraftCreated={handleDraftCreated}
        onConversationUpdate={handleConversationUpdate}
        onMarkRead={markSelectedAsRead}
      >
        <div className="panes d-flex flex-grow-1 overflow-hidden">
          {/* Left: conversations list */}
          <div className="conversations-pane d-flex flex-column border-end h-100 overflow-hidden">
            {/* Inbox title + three-dot menu */}
            <div className="d-flex justify-content-between align-items-center" style={{ padding: '12px 32px' }}>
              <h6 className="fw-bold mb-0">Inbox</h6>

              <InboxDropdownMenu
                onToggleSelectionMode={selection.toggleSelectionMode}
                onMarkAllReadComplete={handleMarkAllReadComplete}
                selectionMode={selection.selectionMode}
                trashTabActive={mailbox.showTrashed}
              />
            </div>

            {/* Toolbar (always visible) + selection controls underneath when active */}
            <ConversationsToolbar
              activeFilters={activeFilters}
              searchValue={searchQuery}
              onSearchChange={handleSearchQueryChange}
              onFilterChange={handleFilterChange}
              isMedia={isMedia}
            />

            {selection.selectionMode && (
              <SelectionBar
                selectedCount={selection.selectedCount}
                totalCount={allSelectableIds.length}
                hasPitchesSelected={selection.hasPitchesSelected}
                onClose={selection.toggleSelectionMode}
                onSelectAll={selection.selectAll}
                onDeselectAll={selection.deselectAll}
                onMarkRead={selection.bulkMarkRead}
                onArchive={selection.bulkArchive}
                isMarkingRead={selection.isMarkingRead}
                isArchiving={selection.isArchiving}
              />
            )}

            {/* Search spans every tab (the endpoint is tab-agnostic), so the tabs hide
                while a query is active; selecting a result or clearing brings them back. */}
            {!searchQuery && (
              <MailboxTabs
                activeMailbox={mailbox.activeMailbox}
                onSelect={mailbox.selectMailbox}
                counts={mailbox.counts}
                isMedia={isMedia}
              />
            )}

            {/* The retention notice belongs to the Trash tab, and search leaves tab-land */}
            {mailbox.showTrashed && !searchQuery && (
              <div
                className="d-flex justify-content-center align-items-start gap-2 flex-shrink-0 px-4 border-bottom border-warning-30 bg-warning-10"
                style={{ paddingTop: '12px', paddingBottom: '12px' }}
                role="status"
              >
                <p className="paragraph-sm text-default text-center mb-0">
                  Messages are automatically deleted after 30 days in Trash.
                </p>
              </div>
            )}

            {/* Conversations list */}
            <div className="flex-grow-1 overflow-auto">
              <ConversationsList
                muteReport={muteReport}
                conversations={searchQuery ? visibleSearchResults : mergedFeed.rows}
                selectedConversation={selectedConversation}
                setConversation={searchQuery ? handleSelectSearchResult : handleSelectConversation}
                onConversationActionComplete={handleConversationActionComplete}
                selectionMode={selection.selectionMode}
                selectedConversationIds={selection.selectedConversationIds}
                onToggleConversationSelection={selection.toggleConversationSelection}
                searchQuery={searchQuery}
                isSearching={isSearching}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearActiveFilters}
                activeMailbox={mailbox.activeMailbox}
                onLoadMore={searchQuery ? loadMoreSearchResults : handleLoadMore}
                hasMore={searchQuery ? hasMoreSearchResults : mergedFeed.hasMore}
                isLoadingMore={
                  searchQuery ? isLoadingMoreSearchResults : isLoadingMoreConversations || virtualPitches.isLoadingMore
                }
                isLoading={
                  searchQuery ? false : isLoadingConversations || (virtualRowsEnabled && virtualPitches.isLoading)
                }
                isMedia={isMedia}
                onSelectPitchRow={searchQuery ? handleSelectPitchSearchResult : handleSelectPitchRow}
                selectedPitchId={selectedPitch?.id ?? null}
              />
            </div>
          </div>

          {/* Right: conversation area — clicking marks an auto-selected unread conversation as read */}
          <div
            className="conversation-area d-flex flex-column flex-grow-1 h-100 overflow-hidden"
            style={{ minWidth: 0 }}
            onClickCapture={handleConversationAreaClick}
          >
            {selectedPitch && (
              <PitchThreadPane
                key={selectedPitch.id}
                pitch={selectedPitch}
                conversation={null}
                request={{ slug: selectedPitch.request_slug, title: selectedPitch.request_title }}
                onConversationCreated={refetchConversationsAndCounts}
                onBack={() => setSelectedPitch(null)}
                muteReport={muteReport}
              />
            )}
            {!selectedPitch && rightPaneConversation && (
              <ConversationHeader
                conversation={rightPaneConversation}
                onReloadConversation={draftConversation ? undefined : refetchConversationsAndCounts}
                onActionComplete={draftConversation ? undefined : handleConversationActionComplete}
                sidebarExpanded={sidebarExpanded}
                onToggleSidebar={draftConversation ? undefined : toggleSidebar}
                onBack={draftConversation ? finishCompose : clearSelection}
                activeMailbox={mailbox.activeMailbox}
                isDraft={Boolean(draftConversation)}
                pitchedEntities={headerPitchedEntities}
              />
            )}

            {!selectedPitch && (
              <div className="conversation-body d-flex flex-grow-1 overflow-hidden">
                {/* Messages + composer */}
                <div className="messages-column d-flex flex-column flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                  {liveSelectedConversation?.receipt_archived_at && (
                    <div
                      className="d-flex align-items-start gap-2 flex-shrink-0 border-bottom border-neutral-20 px-4 py-2 bg-neutral-10"
                      role="status"
                    >
                      <p className="paragraph-sm text-secondary mb-0">
                        This conversation is in your Trash. Restore or permanently delete it from the list on the left.
                      </p>
                    </div>
                  )}

                  {(isResolvingDraft && !draftConversation) || (isWaitingForDeepLink && !selectedConversation) ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-secondary gap-3"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="spinner-border text-primary" />
                      <p className="paragraph-md mb-0">Loading conversation…</p>
                    </div>
                  ) : (
                    <>
                      {/* Right-pane empty-state mirrors the left list when there's nothing to show.
                        isLoadingConversations gate suppresses the flash on tab switch + initial load. */}
                      {showInboxEmptyState ? (
                        <InboxEmptyState mailbox={mailbox.activeMailbox} isMedia={isMedia} />
                      ) : (
                        <>
                          <Messages conversation={messagePaneConversation} />
                          <MessageComposer disabled={!messagePaneConversation} conversation={messagePaneConversation} />
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Profile sidebar */}
                {sidebarExpanded && selectedConversation && (
                  <div className="sidebar-pane d-flex flex-column overflow-auto flex-shrink-0 bg-white">
                    <ConversationSidebar conversation={liveSelectedConversation} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </MessagesProvider>
    </div>
  );
}

export default withV2HealthCheck(withV2ProfileSlug(MyInbox));
